import { useTranslation } from "react-i18next";
import clsx from "clsx";

// Entries and their sub-lines reuse the Trackcast page's board strings so the
// two pages never disagree about what each form factor is.
const ROWS = [
  {
    key: "device",
    fastest: true,
    gap: "0.2–0.3 s",
    distance: "11–17 m",
    meter: "7%",
  },
  {
    key: "app",
    fastest: false,
    gap: "0.5–0.7 s",
    distance: "28–39 m",
    meter: "15%",
  },
  {
    key: "platform",
    fastest: false,
    gap: "2–5 s",
    distance: "110–280 m",
    meter: "90%",
  },
] as const;

export default function HomeTimingBoard() {
  const { t } = useTranslation("pages");

  return (
    <section className="bg-gray-950 text-white">
      <div className="mx-auto grid max-w-6xl items-center gap-8 px-6 py-16 lg:grid-cols-[1fr_1.5fr] lg:gap-12 lg:py-20">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-gray-400">
            {t("home.board.eyebrow")}
          </p>
          <h2 className="mb-3 mt-3 text-3xl font-bold tracking-tight text-balance sm:text-4xl">
            {t("home.board.title")}
          </h2>
          <p className="max-w-[40ch] text-gray-400">{t("home.board.lead")}</p>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-800 bg-gray-900/60">
          <table
            className="w-full border-collapse font-mono text-[12.5px]"
            aria-label={t("trackcast.board.colGap")}
          >
            <thead>
              <tr className="border-b border-gray-800 text-[10px] uppercase tracking-[0.12em] text-gray-500">
                <th scope="col" className="px-4 py-2.5 text-left font-normal">
                  {t("trackcast.board.colPos")}
                </th>
                <th scope="col" className="px-4 py-2.5 text-left font-normal">
                  {t("trackcast.board.colEntry")}
                </th>
                <th
                  scope="col"
                  className="hidden whitespace-nowrap px-4 py-2.5 text-left font-normal sm:table-cell"
                >
                  {t("trackcast.board.colGap")}
                </th>
                <th
                  scope="col"
                  className="whitespace-nowrap px-4 py-2.5 text-right font-normal"
                >
                  {t("home.board.colAt200")}
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => (
                <tr
                  key={row.key}
                  className="border-b border-gray-800 last:border-b-0"
                >
                  <td className="px-4 py-3 font-semibold text-gray-300">
                    {i + 1}
                  </td>
                  <td className="px-4 py-3">
                    <span className="block font-sans text-sm font-semibold tracking-[-0.01em]">
                      {t(`trackcast.board.${row.key}Entry`)}
                      {row.fastest && (
                        <span className="ml-2 rounded border border-violet-400 px-1.5 py-px align-middle text-[9px] tracking-[0.12em] text-violet-400">
                          {t("trackcast.board.fastest")}
                        </span>
                      )}
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      {t(`trackcast.board.${row.key}Sub`)}
                    </span>
                  </td>
                  <td className="hidden px-4 py-3 sm:table-cell">
                    <span className="relative block h-1.5 overflow-hidden rounded-full bg-gray-800">
                      <span
                        className={clsx(
                          "absolute inset-y-0 left-0 rounded-full",
                          row.fastest ? "bg-violet-400" : "bg-gray-500",
                        )}
                        style={{ width: row.meter }}
                      />
                    </span>
                  </td>
                  <td
                    className={clsx(
                      "whitespace-nowrap px-4 py-3 text-right text-sm font-semibold tabular-nums",
                      row.fastest ? "text-violet-400" : "text-gray-200",
                    )}
                  >
                    {row.gap}
                    <small className="block text-[10.5px] font-normal text-gray-500">
                      {t("trackcast.board.distanceAt200", {
                        distance: row.distance,
                      })}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
